import {
    Grid,
    SelectChangeEvent
} from "@mui/material";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFTextField } from "../../../lib/formik";
import { TextDatePicker } from "../../onboarding/DatePickerWithTextFeild";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import dayjs from "dayjs";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import {
    CounterPartyBank,
    DematDetailsObj,
    GetDematDetailsResponseBody,
    GetOrderEntryBanksListResponseBody, GetSettlementDetailsResponseBody
} from "../../../redux-store/types/orderManagement";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { MPOWER_ACCOUNT_TYPE_MASTER } from "../../../utils/constant";
import * as yup from 'yup';
import { Dispatch, SetStateAction, useRef } from "react";
import { getDematDetails } from "../../../redux-store/actions/orderManagement";
import { showError } from "../../../redux-store/actions/auth";

const DEPOSITORY_NAME_LIST = [
    {
        key: "CDSL",
        value: "CDSL"
    }, {
        key: "NSDL",
        value: "NSDL"
    }
]

export default function SettlementDetailsForm({
    fetchSettlementDetails,
    settlementDetailsList,
    activeProductType,
    setValidations,
    bankAccountList,
    isLoading,
    setLoadingStatus
}: {
    settlementDetailsList: GetSettlementDetailsResponseBody
    fetchSettlementDetails: (searchInput: string) => void;
    activeProductType: string;
    setValidations: (values: Record<string, any>) => void;
    bankAccountList: GetOrderEntryBanksListResponseBody;
    isLoading: boolean;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
}): JSX.Element {
    const depositoryNameRef = useRef<HTMLElement>(null)

    const dispatch = useDispatch();

    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        setFieldValue,
        values,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleSettlementDateChange = (selectedDate: any) => {
        setFieldValue('settlementDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleSettlementBankNumberChange = async (selectedObj: Record<string, any>) => {
        if (["Corpus In/Out"].includes(activeProductType)) {
            resetForm({
                values: {
                    ...values,
                    settlementBankNumber: selectedObj,
                    settlementBank: selectedObj?.bankname ?? ""
                }
            })
            return
        }

        resetForm({
            values: {
                ...values,
                settlementBankNumber: selectedObj,
                settlementBank: selectedObj?.bankName ?? ""
            }
        })
    }

    const handleSettlementMethodChange = async (event: SelectChangeEvent<unknown>) => {
        if (event.target.value === "Client Offline Transfer") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                settlementBankNumber: yup
                    .object()
                    .nullable()
                    .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                        const { parent } = context;
                        return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
                    })
            }))
        } else {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                settlementBankNumber: yup
                    .object()
                    .nullable()
                    .test(
                        "settlement-bank-number-required",
                        "Settlement Bank Number is required",
                        function (value) {
                            return !!value
                        })
                    .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                        const { parent } = context;
                        return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
                    })
            }))
        }

        resetForm({
            values: {
                ...values,
                settlementMethod: event.target.value,
            }
        })
    }

    const handleDepositoryNameChange = async (event: SelectChangeEvent<unknown>) => {
        try {
            setLoadingStatus("apiLoading")

            setFieldValue("settlementDetailsDepositoryName", event.target.value)

            const payload = {
                portfolioId: values?.account ?? "",
                clientCode: values?.clientName?.clientCode ?? "",
                depositoryName: event.target.value as string
            }

            const getDematDetailsResponse = await (dispatch(getDematDetails(payload)) as unknown as Promise<GetDematDetailsResponseBody>);

            if (!getDematDetailsResponse?.isSuccess) {
                dispatch(showError(getDematDetailsResponse?.message ?? ""))
            }

            resetForm({
                values: {
                    ...values,
                    settlementDetailsDepositoryName: event.target.value,
                    settlementDetailsDpNameList: getDematDetailsResponse?.result ?? [],
                    settlementDetailsDpName: null,
                    settlementDetailsDpId: "",
                    settlementDetailsClientId: ""
                }
            })
        } catch (error) {
            console.log(error ?? "")
        } finally {
            setLoadingStatus("")
            setTimeout(() => {
                setFieldValue("settlementDetailsDepositoryName", event.target.value)
                depositoryNameRef.current?.focus()
            }, 5)
        }
    }

    const handleDpNameChange = (selectedObj: Record<string, any>) => {
        if (!selectedObj) {
            resetForm({
                values: {
                    ...values,
                    settlementDetailsDpName: null,
                    settlementDetailsDpId: "",
                    settlementDetailsClientId: ""
                }
            })

            return
        }

        resetForm({
            values: {
                ...values,
                settlementDetailsDpName: selectedObj as DematDetailsObj,
                settlementDetailsDpId: selectedObj?.depositoryId ?? "",
                settlementDetailsClientId: selectedObj?.clientId ?? ""
            }
        })

    }

    let settlementMethodDropdownOptions = [
        { name: "Online Transfer" },
        { name: "Initiate Transfer" },
        { name: "Client Offline Transfer" }
    ]

    // NON POA CASE
    if (MPOWER_ACCOUNT_TYPE_MASTER[(values?.accountType ?? "")?.toUpperCase()] !==
        MPOWER_ACCOUNT_TYPE_MASTER["POA"]
    ) {
        settlementMethodDropdownOptions = [
            { name: "Online Transfer" }
        ]
    }
    // POA CASE
    else if (MPOWER_ACCOUNT_TYPE_MASTER[(values?.accountType ?? "")?.toUpperCase()] ===
        MPOWER_ACCOUNT_TYPE_MASTER["POA"]
    ) {
        if (activeProductType === "Corpus In/Out") {
            if (values?.transactionType === "Corpus In") {
                settlementMethodDropdownOptions = [
                    { name: "Client Offline Transfer" }
                ]
            } else if (values?.transactionType === "Corpus Out") {
                settlementMethodDropdownOptions = [
                    { name: "Initiate Transfer" },
                    { name: "Client Offline Transfer" }
                ]
            }
        } else if (["Unlisted Equity", "AIF", "PMS", "Bonds", "Margin Money"].includes(activeProductType)) {
            // Sell type
            if (["Sell", "Redemption", "Withdrawal", "Cash In Broker"].includes(values?.transactionType)) {
                settlementMethodDropdownOptions = [
                    { name: "Online Transfer" },
                ]
            }
            // Buy type
            else {
                settlementMethodDropdownOptions = [
                    { name: "Initiate Transfer" },
                    { name: "Client Offline Transfer" },
                ]
            }
        }
    }

    const settlementDetailsListFromSecurity = [{
        bankCode: values?.security?.bankCode ?? "",
        bankAccCcy: values?.security?.bankAccCcy ?? "",
        bankAccType: values?.security?.bankAccType ?? "",
        bankAccNo: values?.security?.bankAccNo ?? "",
        bankAccCode: values?.security?.bankAccCode ?? "",
        bankName: values?.security?.bankName ?? "",
        label: values?.security?.bankAccNo ?? "",
        value: values?.security?.bankAccNo ?? ""
    }]

    const settlementDetailsListFromBrokerBankDetails = [{
        ...(values?.brokerBankDetails ?? {}),
        label: values?.brokerBankDetails?.bankAccNo ?? "",
        value: values?.brokerBankDetails?.bankAccNo ?? ""
    }]

    const settlementDetailsListFromCounterParty = (values?.counterParty?.banks ?? [])?.map((obj: CounterPartyBank) => ({
        ...obj,
        label: obj?.bankAccNo ?? "",
        value: obj?.bankAccNo ?? ""
    }))

    let settlementBankNumberOptionsList: Array<Record<string, any>> = settlementDetailsList?.settlementBanks ?? []

    if (["Unlisted Equity"].includes(activeProductType)) {
        settlementBankNumberOptionsList = settlementDetailsListFromCounterParty
    }
    if (["AIF", "PMS", "Bonds"].includes(activeProductType)) {
        settlementBankNumberOptionsList = settlementDetailsListFromSecurity ?? []
    }
    if (["Margin Money"].includes(activeProductType)) {
        settlementBankNumberOptionsList = settlementDetailsListFromBrokerBankDetails ?? []
    }
    if (["Corpus In/Out"].includes(activeProductType)) {
        settlementBankNumberOptionsList = (bankAccountList?.bankDetails ?? [])
    }

    return (
        <Grid
            container
            columnSpacing={3}
        >
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="settlementMethod"
                    label="Settlement Method *"
                    items={settlementMethodDropdownOptions.map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.accountType || !values?.transactionType}
                    onChange={handleSettlementMethodChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="settlementDate"
                    label="Settlement Date *"
                    placeholder=""
                    maxDate={new Date("2099-12-31")}
                    minDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleSettlementDateChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="referenceNumber"
                    label="Reference Number"
                    placeholder=""
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="settlementBankNumber"
                    label={`Settlement Bank Number ${values?.settlementMethod === "Client Offline Transfer" ? "" : "*"}`}
                    options={(settlementBankNumberOptionsList ?? [])
                        ?.filter((obj: Record<string, any>) => obj?.bankAccNo)
                        ?.map((obj: Record<string, any>) => ({
                            ...obj,
                            label: obj?.bankAccNo ?? "",
                            value: obj?.bankAccNo ?? ""
                        })) ?? []}
                    disabled={
                        isLoading ||
                            ((["AIF", "PMS", "Bonds"].includes(activeProductType)) ? !values?.security : false) ||
                            (["Unlisted Equity"].includes(activeProductType) ? !values?.counterParty : false) ||
                            ["Margin Money"].includes(activeProductType) ? !values?.broker : false ||
                                ["Corpus In/Out"].includes(activeProductType) ? !values?.investorBankAccount : false
                    }
                    onChange={handleSettlementBankNumberChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="settlementBank"
                    label="Settlement Bank"
                    placeholder=""
                    disabled={true}
                />
            </Grid>
            {["Unlisted Equity", "AIF", "Bonds"].includes(activeProductType) && (
                <>
                    <Grid item xs={12} lg={4}>
                        <MFSelectField
                            inputRef={depositoryNameRef}
                            name="settlementDetailsDepositoryName"
                            label="Depository Name"
                            items={DEPOSITORY_NAME_LIST}
                            disabled={isLoading || !values.clientName || !values.account}
                            onChange={handleDepositoryNameChange}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <FormikSearchDropdown
                            name="settlementDetailsDpName"
                            label="DP Name"
                            options={(values?.settlementDetailsDpNameList ?? [])?.map((obj: DematDetailsObj) => ({
                                ...obj,
                                label: obj?.dpName ?? "",
                                value: obj?.dpName ?? "",
                            })) ?? []}
                            onChange={handleDpNameChange}
                            disabled={isLoading || !values.settlementDetailsDepositoryName}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="settlementDetailsDpId"
                            label="DP ID"
                            placeholder=""
                            disabled={isLoading || !values.settlementDetailsDpName}
                            value={values?.settlementDetailsDpId ?? ""}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="settlementDetailsClientId"
                            label="Client ID"
                            placeholder=""
                            disabled={isLoading || !values.settlementDetailsDpName}
                            value={values?.settlementDetailsClientId ?? ""}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
}